import type { Socket } from "socket.io-client";

/**
 * Keeps a portal's socket alive and tells the app when it may have missed
 * events. Shared by the patient, clinician and admin sockets.
 *
 * 1. A refused (re)connect is retried. socket.io-client only retries network
 *    failures on its own: when the server answers a reconnect with an error
 *    (an expired token, the database briefly unreachable during the account
 *    check), the client destroys the socket for good — and nothing reopened
 *    it until the next token change, up to ~15 minutes later. Live updates
 *    silently stopped until then (or a page refresh).
 * 2. Anything sent while the socket was down is never replayed, and the
 *    server also broadcasts "realtime:resync" when its own event relay had a
 *    gap. Either way, every "resync" listener is called so screens refetch.
 */

export const RESYNC_EVENT = "realtime:resync";
const RETRY_MIN_MS = 2_000;
const RETRY_MAX_MS = 30_000;

const resyncListeners = new WeakMap<Socket, Set<() => void>>();
const hardened = new WeakSet<Socket>();

function fireResync(socket: Socket) {
  for (const listener of resyncListeners.get(socket) ?? []) {
    try {
      listener();
    } catch (error) {
      console.error("[realtime] resync listener failed", error);
    }
  }
}

/** Calls `listener` whenever `socket` may have missed events. Returns an unsubscribe. */
export function onResync(socket: Socket, listener: () => void): () => void {
  let set = resyncListeners.get(socket);
  if (!set) resyncListeners.set(socket, (set = new Set()));
  set.add(listener);
  return () => {
    set.delete(listener);
  };
}

/**
 * Wires recovery onto `socket` once. `hasSession` says whether the portal is
 * still signed in — no retries after a logout.
 */
export function hardenSocket(socket: Socket, hasSession: () => boolean): void {
  if (hardened.has(socket)) return;
  hardened.add(socket);

  let connectedBefore = false;
  let retryDelay = RETRY_MIN_MS;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  socket.on("connect", () => {
    retryDelay = RETRY_MIN_MS;
    if (retryTimer) clearTimeout(retryTimer);
    retryTimer = null;
    // The first connect needs no resync — every screen just loaded its data.
    if (connectedBefore) fireResync(socket);
    connectedBefore = true;
  });

  socket.on("connect_error", () => {
    // Still active = a network failure socket.io keeps retrying by itself.
    if (socket.active || retryTimer || !hasSession()) return;
    retryTimer = setTimeout(() => {
      retryTimer = null;
      if (!socket.connected && hasSession()) socket.connect();
    }, retryDelay);
    retryDelay = Math.min(retryDelay * 2, RETRY_MAX_MS);
  });

  socket.on(RESYNC_EVENT, () => fireResync(socket));
}
